import { useCallback, useEffect, useRef, useState } from 'react'
import type { RealtimeChannel } from '@supabase/supabase-js'
import type { CommandEnvelope, CommandSurface } from '@square/core/commands'
import { useLocation, useNavigate } from 'react-router-dom'
import { api, ApiError, isAuthError, type CommandApiResult, type CommandIntent } from '../api/client'
import { getRuntime } from './_mode'
import { captureHookFailure, recordHookOutcome, recordShellAction } from '../observability/sentry'

interface RouteContext {
  surface: CommandSurface
  projectId: string | null
}

// When a typed request matches no deterministic command, the bar hands
// it to the Compass interpreter and turns the outcome into one of these
// VISIBLE results — a typed request never silently does nothing.
export type CommandInterpretation =
  | { kind: 'proposal'; intent: CommandIntent }        // interpreted as a runnable command → offer Run
  | { kind: 'answer'; message: string }                // conversational reply from the model
  | { kind: 'unrecognized'; message: string }          // model could not classify → offer Agent lane
  | { kind: 'needs_setup'; message: string }           // ANTHROPIC_API_KEY / auth missing (honest blocked)

export interface CommandDispatchState {
  status: 'idle' | 'dispatching' | 'interpreting' | string
  message: string | null
  result: CommandApiResult | null
  interpretation: CommandInterpretation | null
  lastInput: string | null
}

function inferRouteContext(pathname: string): RouteContext {
  const studio = pathname.match(/^\/square\/projects\/([^/]+)\/studio$/)
  if (studio) return { surface: 'workspace', projectId: studio[1] }

  const site = pathname.match(/^\/square\/projects\/([^/]+)\/site$/)
  if (site) return { surface: 'site', projectId: site[1] }

  const project = pathname.match(/^\/square\/projects\/([^/]+)(?:\/hosting|\/settings|\/comms)?$/)
  if (project) return { surface: 'project', projectId: project[1] }

  if (pathname === '/square/workspace' || pathname === '/square/quamspace') {
    return { surface: 'workspace', projectId: null }
  }

  return { surface: 'shell', projectId: null }
}

function isTerminal(status: string): boolean {
  return (
    status === 'done' ||
    status === 'error' ||
    status === 'cancelled' ||
    status === 'unknown'
  )
}

function commandFromRow(row: Record<string, any>): CommandApiResult {
  return {
    id: row.id,
    status: row.status,
    command_id: row.command_id ?? null,
    args: row.args_json ?? {},
    message:
      (row.result_json && typeof row.result_json.message === 'string'
        ? row.result_json.message
        : null) ??
      (row.error_json && typeof row.error_json.message === 'string'
        ? row.error_json.message
        : null),
    result: row.result_json ?? {},
    error: row.error_json ?? null,
    job_refs: row.job_refs_json ?? {},
    voice_output_url: row.voice_output_url ?? null,
    created_at: row.created_at,
    completed_at: row.completed_at ?? null,
  }
}

export function useCommandDispatch() {
  const location = useLocation()
  const navigate = useNavigate()
  const [state, setState] = useState<CommandDispatchState>({
    status: 'idle',
    message: null,
    result: null,
    interpretation: null,
    lastInput: null,
  })
  const channelRef = useRef<RealtimeChannel | null>(null)

  const closeChannel = useCallback(() => {
    const { supabase } = getRuntime()
    if (channelRef.current && supabase) {
      supabase.removeChannel(channelRef.current)
    }
    channelRef.current = null
  }, [])

  useEffect(() => closeChannel, [closeChannel])

  const subscribe = useCallback(
    (commandId: string) => {
      const { live, supabase } = getRuntime()
      if (!live || !supabase) return

      closeChannel()

      try {
        channelRef.current = supabase
          .channel(`square-shell-command-${commandId}`)
          .on(
            'postgres_changes' as any,
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'sq_commands',
              filter: `id=eq.${commandId}`,
            },
            (payload: { new: Record<string, any> }) => {
              const next = commandFromRow(payload.new)
              setState((prev) => ({
                status: next.status,
                message:
                  next.message ??
                  (typeof next.error?.message === 'string'
                    ? String(next.error.message)
                    : null),
                result: next,
                interpretation: null,
                lastInput: prev.lastInput,
              }))

              const navigateTo = next.result?.navigate
              if (typeof navigateTo === 'string' && next.status === 'done') {
                navigate(navigateTo)
              }

              if (isTerminal(next.status)) closeChannel()
            },
          )
          .subscribe()
      } catch (err) {
        captureHookFailure('useCommandDispatch', 'realtime_failure', err, {
          table: 'sq_commands',
          route: location.pathname,
        })
      }
    },
    [closeChannel, location.pathname, navigate],
  )

  const resolveSurfaceId = useCallback(async (ctx: RouteContext): Promise<string | null> => {
    if (ctx.surface === 'project') return ctx.projectId

    if (ctx.surface === 'workspace') {
      const result = await api.workspace.list({
        scope: ctx.projectId ? 'project' : 'personal',
        project_id: ctx.projectId ?? undefined,
        limit: 1,
      })
      return result.workspaces?.[0]?.id ?? null
    }

    if (ctx.surface === 'site' && ctx.projectId) {
      const result = await api.site.get(ctx.projectId)
      return result.site?.id ?? null
    }

    return null
  }, [])

  // Compass fallback — when deterministic dispatch returns `unknown`,
  // interpret the utterance with the server-side Claude interpreter and
  // turn the outcome into a VISIBLE result. This never executes; it
  // classifies and (for a command) proposes a Run the operator confirms.
  const interpretFallback = useCallback(
    async (input: string, ctx: RouteContext, surfaceId: string | null) => {
      setState((prev) => ({ ...prev, status: 'interpreting', message: 'interpreting…' }))
      try {
        const { intent } = await api.commands.interpret({
          input_text: input,
          surface: ctx.surface,
          surface_id: surfaceId,
        })

        let interpretation: CommandInterpretation
        if (intent.mode === 'command' && intent.command_id) {
          interpretation = { kind: 'proposal', intent }
        } else if (intent.mode === 'answer' && intent.message) {
          interpretation = { kind: 'answer', message: intent.message }
        } else {
          interpretation = {
            kind: 'unrecognized',
            message:
              intent.message ??
              "I couldn't map that to a command. Send it to an agent to work on it.",
          }
        }

        setState((prev) => ({
          ...prev,
          status: interpretation.kind,
          message: interpretation.kind === 'proposal' ? (intent.message ?? null) : (('message' in interpretation) ? interpretation.message : null),
          interpretation,
        }))
        recordShellAction('command_bar.interpret', {
          command: input.slice(0, 120),
          surface: ctx.surface,
          mode: intent.mode,
          command_id: intent.command_id ?? undefined,
          confidence: intent.confidence,
        })
      } catch (err) {
        const apiErr = err as ApiError
        // Honest blocked states — name the exact missing dependency.
        let message: string
        if (isAuthError(err)) {
          message = 'Sign in to use plain-language requests.'
        } else if (apiErr?.status === 503) {
          message =
            'Plain-language understanding needs ANTHROPIC_API_KEY configured on the server. This request was not a known command — send it to an agent instead.'
        } else {
          message =
            'Interpretation is unavailable right now. This request was not a known command — send it to an agent, or use a command.'
        }
        setState((prev) => ({
          ...prev,
          status: 'needs_setup',
          message,
          interpretation: { kind: 'needs_setup', message },
        }))
      }
    },
    [],
  )

  const execute = useCallback(
    async (raw: string) => {
      const input = raw.trim()
      if (!input) return null

      const startedAt = Date.now()
      setState({ status: 'dispatching', message: 'dispatching command…', result: null, interpretation: null, lastInput: input })

      try {
        const ctx = inferRouteContext(location.pathname)
        const surfaceId = await resolveSurfaceId(ctx)

        const envelope: CommandEnvelope = {
          surface: ctx.surface,
          surface_id: surfaceId,
          input_source: 'text',
          input_text: input,
          metadata: {
            route: location.pathname,
            project_id: ctx.projectId ?? undefined,
          },
        }

        const response = await api.commands.dispatch(envelope as unknown as Record<string, unknown>)
        const result = response.result

        setState({
          status: result.status,
          message:
            result.message ??
            (typeof result.error?.message === 'string'
              ? String(result.error.message)
              : null),
          result,
          interpretation: null,
          lastInput: input,
        })

        recordShellAction('command_bar.execute', {
          command: input.slice(0, 120),
          surface: envelope.surface,
          surface_id: envelope.surface_id ?? undefined,
          command_id: result.command_id,
          status: result.status,
        })
        recordHookOutcome('useCommandDispatch', 'live_success', {
          route: '/api/commands/dispatch',
          durationMs: Date.now() - startedAt,
          status: result.status,
        })

        const navigateTo = result.result?.navigate
        if (typeof navigateTo === 'string' && result.status === 'done') {
          navigate(navigateTo)
        }

        // Deterministic miss → hand to the Compass interpreter so the
        // bar always produces a visible next step instead of a dead end.
        if (result.status === 'unknown') {
          await interpretFallback(input, ctx, surfaceId)
          return result
        }

        if (!isTerminal(result.status)) subscribe(result.id)
        else closeChannel()

        return result
      } catch (err) {
        const apiErr = err as ApiError
        setState((prev) => ({
          status: 'error',
          message: apiErr?.message || 'command_dispatch_failed',
          result: null,
          interpretation: null,
          lastInput: prev.lastInput,
        }))
        captureHookFailure('useCommandDispatch', 'live_error', err, {
          route: '/api/commands/dispatch',
          durationMs: Date.now() - startedAt,
          code: apiErr?.code,
          status: apiErr?.status,
        })
        return null
      }
    },
    [closeChannel, interpretFallback, location.pathname, navigate, resolveSurfaceId, subscribe],
  )

  // Run a confirmed interpretation. Dispatches the pre-resolved
  // command_id + args through the SAME governed dispatch route — the
  // server re-validates the id for the surface and enforces voice_safe;
  // this is not a bypass, it's the interpreter's Run button.
  const dispatchProposal = useCallback(
    async (intent: CommandIntent) => {
      if (intent.mode !== 'command' || !intent.command_id) return null
      const input = state.lastInput ?? intent.message ?? intent.command_id
      const startedAt = Date.now()
      setState((prev) => ({ ...prev, status: 'dispatching', message: 'running…', interpretation: null }))
      try {
        const ctx = inferRouteContext(location.pathname)
        const surfaceId = await resolveSurfaceId(ctx)
        const envelope: CommandEnvelope = {
          surface: ctx.surface,
          surface_id: surfaceId,
          input_source: 'text',
          input_text: input,
          command_id: intent.command_id,
          args: intent.args ?? {},
          metadata: { route: location.pathname, project_id: ctx.projectId ?? undefined, via: 'interpret' },
        }
        const response = await api.commands.dispatch(envelope as unknown as Record<string, unknown>)
        const result = response.result
        setState((prev) => ({
          status: result.status,
          message: result.message ?? (typeof result.error?.message === 'string' ? String(result.error.message) : null),
          result,
          interpretation: null,
          lastInput: prev.lastInput,
        }))
        const navigateTo = result.result?.navigate
        if (typeof navigateTo === 'string' && result.status === 'done') navigate(navigateTo)
        if (!isTerminal(result.status)) subscribe(result.id)
        else closeChannel()
        return result
      } catch (err) {
        const apiErr = err as ApiError
        setState((prev) => ({ status: 'error', message: apiErr?.message || 'command_dispatch_failed', result: null, interpretation: null, lastInput: prev.lastInput }))
        captureHookFailure('useCommandDispatch', 'proposal_error', err, {
          route: '/api/commands/dispatch',
          durationMs: Date.now() - startedAt,
          code: apiErr?.code,
        })
        return null
      }
    },
    [closeChannel, location.pathname, navigate, resolveSurfaceId, state.lastInput, subscribe],
  )

  return {
    state,
    execute,
    dispatchProposal,
    clear: () =>
      setState({
        status: 'idle',
        message: null,
        result: null,
        interpretation: null,
        lastInput: null,
      }),
  }
}
